import React, { useMemo } from "react";
import {
  Box,
  EntityGroup,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type EntityGroupItem,
  type EntityGroupSize,
  type SurfaceToken,
} from "@salestrak/ui";

import {
  entityToGroupItem,
  resolveEntityLabel,
  type EntityData,
} from "#app/lib/entity-visuals";
import { useAppData } from "#app/lib/data";
import { useMemberDirectory } from "#app/lib/data/member-directory-provider";
import { formatNameList } from "#app/lib/utils/format-name-list";
import {
  presenceDotProps,
  usePresenceMap,
} from "#app/lib/data/presence-provider";

import { EntitySelectionHover } from "./entity-selection-hover";
import type { Omittable } from "@salestrak/ui";

export type EntityGroupTagProps = Omittable<{
  entities: EntityData[];
  /** The size of the set `entities` samples, when a row could only load the
   *  first few (see EntityGroup.total). The "+N" counts against it. Without
   *  `allIds` the chip can only state the count — a read-out naming 2 of 9
   *  states something false, and the missing 7 were never fetched. */
  total?: number;
  /** Every member id in the set, in display order, when `entities` is only a
   *  preview of it. The "+N" chip's tooltip resolves whoever it hides through
   *  the member directory the moment it opens — one batched lookup, nothing
   *  per row — so a bounded preview still answers "who" like a complete set. */
  allIds?: readonly string[];
  max?: number;
  size?: EntityGroupSize;
  variant?: "stack" | "expanded";
  /** The surface the group sits on — colors the separation rings and tint
   *  bakes. Pass "card" when rendering on a card (kanban cards, list cards). */
  surface?: SurfaceToken;
  title?: string;
  /** Default TRUE: clicking an avatar opens its preview (the only way to
   *  reach the cards on touch, where hover never fires); inside a preview card
   *  it opens the entity's page instead — a card never spawns a card. Pass
   *  false when the parent owns the click (popover/selector cells, clickable
   *  rows). */
  interactive?: boolean;
  /** Suppress the hover list card (e.g. while the owning picker is open and
   *  the trigger is frozen). */
  hoverDisabled?: boolean;
  /** Name everyone in ONE tooltip over the whole group instead — including
   *  whoever the "+N" is hiding. For a pressable row that states its facts
   *  inline: the row is the hover target, and a card opening off one of its
   *  avatars is the very popover those inline facts exist to be rid of. Pair
   *  with `hoverDisabled`; a group still offering the per-face cards has no
   *  use for a second hover layer. */
  tooltip?: boolean;
  /** Live presence dots on member avatars — OPT-IN, off by default. Pass
   *  true only where online state is the point: members/teams rosters and
   *  profile surfaces (see EntityVisual.presence for the surface policy). */
  presence?: boolean;
  /** An action on the SET, trailing the stack in a slot of its own (see
   *  EntityGroup.trailing) — an `EntityGroupAddSlot` opening a picker. With no
   *  entities the group renders it alone rather than nothing, so an empty cell
   *  still offers the action. */
  trailing?: React.ReactNode;
}>;

/**
 * What the "+N" chip's tooltip reads out: the hidden people as one sentence.
 * A set the row holds only as ids resolves them here, when the tooltip opens,
 * so the directory lookup is spent on the roster someone actually asked
 * about; ids the workspace bootstrap already carries answer on the first
 * render, the rest land together as one batch — the sentence grows from the
 * bare count into the names as they arrive.
 */
function OverflowNames({
  ids,
  count,
}: Omittable<{
  ids: readonly string[];
  /** The size of the hidden set — what the chip counts. */
  count: number;
}>) {
  const { request, entityOf } = useMemberDirectory();
  // Safe during render by contract — unknown ids batch on a macrotask.
  request(ids);
  const names: string[] = [];
  for (const id of ids) {
    const entity = entityOf(id);
    if (entity) names.push(resolveEntityLabel(entity));
  }
  return <>{formatNameList(names, count)}</>;
}

export function EntityGroupTag({
  entities,
  total,
  allIds,
  max = 3,
  size = "xs",
  variant = "stack",
  surface,
  title,
  interactive = true,
  hoverDisabled,
  tooltip,
  presence: presenceEnabled = false,
  trailing,
}: EntityGroupTagProps) {
  // Presence dots on member avatars — the group renders through the library
  // EntityGroup (flat item primitives, not EntityVisual), so presence is
  // injected here as the semantic `presence` slot. Nothing for unknown
  // members or while the snapshot is in flight; Avatar itself suppresses the
  // dot for suspended/invited avatars.
  const presenceMap = usePresenceMap();
  const { currentUser } = useAppData();
  const items = useMemo(
    () =>
      entities.map((entity) => {
        let item = entityToGroupItem(entity);
        if (entity.kind !== "user") return item;
        // The viewer's OWN avatar renders themed to them — group
        // stacks never opt into the true color; that's a profile-surface
        // affordance.
        if (currentUser?.userId === entity.id && item.avatarColor) {
          item = { ...item, avatarColor: null };
        }
        if (!presenceEnabled) return item;
        const presence = presenceMap.get(entity.id);
        if (!presence) return item;
        return { ...item, presence: presenceDotProps(presence) };
      }),
    [entities, presenceMap, presenceEnabled, currentUser?.userId],
  );

  const isPartial = total !== undefined && total > entities.length;

  // How many leading faces the group shows before folding the rest into the
  // "+N" chip — "expanded" always folds everything after the first item;
  // "stack" folds once the count exceeds `max` (the chip replaces a slot).
  const shownCount =
    variant === "expanded" ? 1 : entities.length > max || isPartial ? max - 1 : entities.length;

  // Hidden remainder behind the "+N" chip — the only place the list card
  // still renders. A partial set has no truthful card of its own: the hidden
  // entities behind its "+N" were never loaded. An empty list leaves the chip
  // to its ids, or bare.
  const overflowEntities = useMemo(
    () => (isPartial ? [] : entities.slice(shownCount)),
    [entities, isPartial, shownCount],
  );

  // A partial set's hidden members, as ids — everyone in the set who is not
  // one of the faces on show. The card resolves them on open.
  const overflowIds = useMemo(() => {
    if (!isPartial || !allIds) return [];
    const shown = new Set(entities.slice(0, shownCount).map((entity) => entity.id));
    return allIds.filter((id) => !shown.has(id));
  }, [isPartial, allIds, entities, shownCount]);

  // Each visible avatar hovers (or clicks, on touch) to ITS entity's rich
  // preview — the index maps 1:1 to `entities` (entityToGroupItem keeps order).
  const wrapItem = React.useCallback(
    (node: React.ReactNode, _item: EntityGroupItem, index: number) => {
      const entity = entities[index];
      if (!entity) return node;
      return (
        <EntitySelectionHover
          entity={entity}
          disabled={hoverDisabled}
          clickOpensPreview={interactive}
        >
          {node}
        </EntitySelectionHover>
      );
    },
    [entities, hoverDisabled, interactive],
  );

  // The chip's tooltip reads the hidden people out as one sentence. While the
  // group's own roster tooltip speaks (or hovers are off) the chip adds
  // nothing — two hover layers on one 24px target would fight. With nothing
  // to name it states the one fact it has: the exact count the "9+" cap hides.
  const wrapOverflow = React.useCallback(
    (node: React.ReactNode, count: number) => {
      if (hoverDisabled || tooltip) return node;
      const label =
        overflowEntities.length > 0 ? (
          formatNameList(overflowEntities.map(resolveEntityLabel), count)
        ) : overflowIds.length > 0 ? (
          <OverflowNames ids={overflowIds} count={count} />
        ) : (
          `${count} more`
        );
      return (
        <Tooltip>
          <TooltipTrigger
            render={<Box direction="row" align="center" justify="center" fill="size" />}
          >
            {node}
          </TooltipTrigger>
          <TooltipPopup>
            <Box maxWidth={320}>{label}</Box>
          </TooltipPopup>
        </Tooltip>
      );
    },
    [overflowEntities, overflowIds, hoverDisabled, tooltip],
  );

  // A trailing action survives an empty set — it is the whole point of the
  // empty cell. Without one there is nothing to render.
  if (items.length === 0 && !trailing) return null;

  const group = (
    <EntityGroup
      items={items}
      total={total}
      max={max}
      size={size}
      variant={variant}
      surface={surface}
      wrapItem={wrapItem}
      wrapOverflow={wrapOverflow}
      trailing={trailing}
    />
  );

  // The roster tooltip names who is IN the group; with nobody in it there is
  // nothing to name, and the trailing action carries its own label.
  if (!tooltip || items.length === 0) return group;

  return (
    <Tooltip>
      <TooltipTrigger render={<Box direction="row" align="center" />}>
        {group}
      </TooltipTrigger>
      <TooltipPopup side="top">
        <Box gap="2xs" maxWidth={320}>
          {title ? (
            <Text size="2xs" variant="muted">
              {title}
            </Text>
          ) : null}
          {formatNameList(entities.map(resolveEntityLabel))}
        </Box>
      </TooltipPopup>
    </Tooltip>
  );
}
